import React from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
    WarehouseReturnState,
    WarehouseReturnFragmentFragment,
    usePassWarehouseReturnToInProgressMutation,
    usePassWarehouseReturnToDeliveredMutation,
    usePassWarehouseReturnToStoredMutation,
    TotadminPermission,
} from 'data/__generated__';

import { SectionContainer } from 'components/DetailsView/Section';
import { Loader, LoaderModeType } from 'components/Loader';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

export const ActionSection = ({ warehouseReturn }: { warehouseReturn: WarehouseReturnFragmentFragment }) => {
    const [passWarehouseReturnToInProgress, { loading: passWarehouseReturnToInProgressLoading }] =
        usePassWarehouseReturnToInProgressMutation({ variables: { warehouseReturnId: warehouseReturn._id } });
    const [passWarehouseReturnToDelivered, { loading: passWarehouseReturnToDeliveredLoading }] =
        usePassWarehouseReturnToDeliveredMutation({
            variables: { warehouseReturnId: warehouseReturn._id },
        });
    const [passWarehouseReturnToStored, { loading: passWarehouseReturnToStoredLoading }] =
        usePassWarehouseReturnToStoredMutation({
            variables: { warehouseReturnId: warehouseReturn._id },
        });

    function displayToast({ success, errorMessage }: { success: boolean; errorMessage: string }) {
        if (success) {
            toast.success('Le changement de statut a bien été fait !');
        } else {
            toast.error(errorMessage);
        }
    }

    async function passToInProgress() {
        const { data, errors } = await passWarehouseReturnToInProgress();

        displayToast({
            success: !!data?.passWarehouseReturnToInProgress,
            errorMessage: "Une erreur inconnue s'est produite : " + errors?.join(', '),
        });
    }

    async function passToDelivered() {
        const { data, errors } = await passWarehouseReturnToDelivered();

        displayToast({
            success: !!data?.passWarehouseReturnToDelivered,
            errorMessage: "Une erreur inconnue s'est produite : " + errors?.join(', '),
        });
    }

    async function passToStored() {
        const { data, errors } = await passWarehouseReturnToStored();

        displayToast({
            success: !!data?.passWarehouseReturnToStored,
            errorMessage: "Une erreur inconnue s'est produite : " + errors?.join(', '),
        });
    }

    return (
        <SectionContainer title="Actions" isInitiallyOpen>
            <ButtonContainer>
                <TotemPrimaryButton
                    onClick={passToInProgress}
                    requiredPermissions={[TotadminPermission.WarehouseReturnsManage]}
                    disabled={
                        passWarehouseReturnToInProgressLoading || warehouseReturn.state !== WarehouseReturnState.Created
                    }
                >
                    Passer en "In progress" (sur la route)
                    {passWarehouseReturnToInProgressLoading ? <Loader mode={LoaderModeType.Spin} size="22px" /> : null}
                </TotemPrimaryButton>
                <TotemPrimaryButton
                    onClick={passToDelivered}
                    requiredPermissions={[TotadminPermission.WarehouseReturnsManage]}
                    disabled={
                        passWarehouseReturnToDeliveredLoading ||
                        warehouseReturn.state !== WarehouseReturnState.InProgress
                    }
                >
                    Livrer le retour entrepôt (= check des livreurs)
                    {passWarehouseReturnToDeliveredLoading ? <Loader mode={LoaderModeType.Spin} size="22px" /> : null}
                </TotemPrimaryButton>
                <TotemPrimaryButton
                    onClick={passToStored}
                    requiredPermissions={[TotadminPermission.WarehouseReturnsManage]}
                    disabled={
                        passWarehouseReturnToStoredLoading || warehouseReturn.state !== WarehouseReturnState.Delivered
                    }
                >
                    Ranger le retour entrepôt (mise à jour des stock entrepôt)
                    {passWarehouseReturnToStoredLoading ? <Loader mode={LoaderModeType.Spin} size="22px" /> : null}
                </TotemPrimaryButton>
            </ButtonContainer>
        </SectionContainer>
    );
};

const ButtonContainer = styled.div`
    display: flex;
    justify-content: space-around;

    & > :not(:first-child) {
        margin-left: 20px;
    }
`;
